<!-- /src/views/PdfViewer.vue -->
<template>
    <LayoutFullWidth class="layout layout--full">
        <template v-slot:content>
            <div v-if="totalPages > 1" class="pagination top">
                <div class="button__group inline">
                    <button 
                        class="button button--sm button--withIcon button--shadow button--brandPrimary"
                        :class="{'button--isDisabled': currentPage == 1}"
                        @click="getPrevPage()"
                    ><i class="fas fa-angle-left"></i>Previous</button>
                    <button
                        class="button button--sm button--withIcon button--shadow button--brandPrimary"
                        :class="{'button--isDisabled': currentPage >= totalPages}"
                        @click="getNextPage()"
                    >Next<i class="fas fa-angle-right"></i></button>
                    <input
                        type="text"
                        class="inline"
                        ref="currentPageInput"
                        :value="currentPage"
                        @input="updateCurrentPage"
                        @click="selectInput"
                        maxlength="4"
                        size="2"
                    />
                    <span> / {{ totalPages }}</span>
                </div>
            </div>
            <Loader v-if="pageRendering" class="loader" color="gray" />
            <div class="canvasContainer">
                <div v-show="printDisabled">
                    <h1>
                        Be advised printing is not allowed for this document in part or in whole.
                    </h1>
                </div>
                <canvas v-show="!printDisabled" id="pdfViewer"></canvas>
            </div>
            <div class="builder">
                <canvas v-if="pageRendering" id="watermark"></canvas>
                <canvas v-if="pageRendering" id="pdfCanvas"></canvas>
            </div>

        </template>
    </LayoutFullWidth>
</template>

<script>

import * as pdfjs from "pdfjs-dist/legacy/build/pdf";
// import {PDFViewer} from "pdfjs-dist/legacy/web/pdf_viewer";
import "pdfjs-dist/legacy/web/pdf_viewer.css";
import LayoutFullWidth from '@/components/layouts/FullWidth'
import Loader from '@/components/common/Loaders/Loader'



pdfjs.GlobalWorkerOptions.workerSrc = "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.14.305/build/pdf.worker.min.js";

export default {
  props: {
    path: {
      type: String,
      required: true,
    },
    isSecure: {
      type: Boolean,
    },
  },

  data() {
      return {
        currentPage: 1,
        totalPages: 1,
        pdfDoc: null,
        pageRendering: true,
        pageNumPending: null,
        printDisabled: false,
        scale: 2,
        pdfCanvas: null,
        pdfContext: null,
        watermarkCanvas: null,
        watermarkContext: null,
        pdfViewerCanvas: null,
        pdfViewerContext: null,
        timeout: null,
        specialEventWatchers: true,
      }
  },

  computed: {
    user() {
      return this.$store.getters['user/getUser']
    },

  },

  methods: {
    async initPDF(path) {
      let loadingTask = pdfjs.getDocument(path)
      const pdf = await loadingTask.promise
      this.pdfDoc = pdf
      this.totalPages = pdf.numPages;
      this.renderPage()
    },

    selectInput(e) {
        e.srcElement.focus();
        e.srcElement.select();
    },

    async renderPage() {
        this.pdfDoc.getPage(this.currentPage).then(page => {
            this.pageRendering = true;

            var viewport = page.getViewport({
                scale: this.scale
            });
            // define canvases 3 canvases are needed to build watermarkCanvas
            this.pdfCanvas.height = viewport.height;
            this.pdfCanvas.width = viewport.width;
            this.watermarkCanvas.height = viewport.height;
            this.watermarkCanvas.width = viewport.width;
            this.pdfViewer.height = viewport.height;
            this.pdfViewer.width = viewport.width;
            // Render PDF page into canvas pdfContext
            var renderContext = {
                canvasContext: this.pdfContext,
                viewport
            };

            var renderTask = page.render(renderContext);
            renderTask.promise.then(() => {
                this.processWatermark()

                this.pageRendering = false;
                // keeps user from rendering next page if already processing previous page
                if (this.pageNumPending !== null) {
                    // New page rendering is pending
                    this.renderPage();
                    this.pageNumPending = null;
                }
            });
        });

    },

    processWatermark() {
        // rotate and center
        this.watermarkContext.translate(this.watermarkCanvas.width / 2, this.watermarkCanvas.height / 2);
        this.watermarkContext.rotate(-0.07 * Math.PI);
        this.watermarkContext.textAlign = "center";
        // create primary line of text
        this.watermarkContext.font = "600 50px Arial";
        this.watermarkContext.fillStyle = "rgba(0, 18, 42, .2)";
        this.watermarkContext.fillText(`CONFIDENTIAL DOCUMENT`, 0, -15);
        // create second line of text
        this.watermarkContext.font = "900 18px Arial";
        this.watermarkContext.fillText(`${this.user.name} | ${this.$moment().format()} | Property of CARR, Inc. Do NOT reproduce.`, 0, 15);
        // merge into final canvas
        this.pdfViewerContext.drawImage(this.pdfCanvas, 0, 0)
        this.pdfViewerContext.drawImage(this.watermarkCanvas, 0, 0)
    },

    queueRenderPage(num) {
        this.currentPage = num
        if (this.pageRendering) {
            this.pageNumPending = num;
        } else {
            this.renderPage();
        }
    },

    getPrevPage() {
        if (this.currentPage <= 1) return
        const down = this.currentPage - 1
        this.queueRenderPage(down);
    },

    getNextPage() {
        if (this.currentPage >= this.totalPages) return
        const up = this.currentPage + 1
        this.queueRenderPage(up);
    },
    updateCurrentPage(e) {
        clearTimeout(this.timeout)
        this.timeout = setTimeout(() => {
            const updatePage = +(e.target.value) > this.totalPages ? this.totalPages : +(e.target.value)
            this.queueRenderPage(updatePage);
        }, 500)
    },
    // @todo this can be deleted
    base64ToBlob( base64, type = "application/octet-stream" ) {
      const mimeType = this.mimeType ? this.mimeType : type
      const binStr = atob( base64 );
      const len = binStr.length;
      const arr = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        arr[ i ] = binStr.charCodeAt( i );
      }
      return new Blob( [ arr ], { type: mimeType } );
    },
  },
  mounted() {
    this.pdfCanvas = document.getElementById('pdfCanvas')
    this.pdfContext = this.pdfCanvas.getContext('2d')
    this.watermarkCanvas = document.getElementById('watermark')
    this.watermarkContext = this.watermarkCanvas.getContext('2d')
    this.pdfViewer = document.getElementById('pdfViewer')
    this.pdfViewerContext = this.pdfViewer.getContext('2d')

    this.initPDF(this.path)

    document.addEventListener('contextmenu', (e) => { 
        if (this.pdfViewer) {
            e.preventDefault();
            e.stopPropagation();
        }
    })
    // looking for keypress to save or print
    window.addEventListener('keydown', e => {
        if (this.pdfViewer) {
            if (!(e.ctrlKey || e.metaKey) && (e.key == 'ArrowRight' )) this.getNextPage()
            if (!(e.ctrlKey || e.metaKey) && (e.key == 'ArrowLeft' )) this.getPrevPage()
            if((e.ctrlKey || e.metaKey) && (e.key == "p" || e.charCode == 16 || e.charCode == 112 || e.keyCode == 80 || e.key == "s") ){
                this.$store.dispatch('notification/add', {
                    type: 'error',
                    msg: `Sorry, ${this.user.first_name}, you are not authorized to Print or Save this Confidential Document`,
                    closable: true,
                    autoClose: false,
                })
                e.cancelBubble = true;
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        }
    })
    // watches for attempt to print
    if (window.matchMedia) {
        window.matchMedia('print').addListener((mql) => {
            if (this.pdfViewer) {
                if (mql.matches) {
                    this.printDisabled = true
                    this.$store.dispatch('notification/add', {
                        type: 'error',
                        msg: `Sorry, ${this.user.first_name}, you are not authorized to Print or Save Confidential Documents.`,
                        closable: true,
                        autoClose: false,
                    })
                } else {
                    this.printDisabled = false
                }
            }
        })
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', e => {})
    document.removeEventListener('contextmenu', e => {})
    window.matchMedia('print').removeListener(mql => {})
    // fallback if listeners are not removed
    this.pdfViewer = false
  },

    components: {
        LayoutFullWidth,
        Loader,
    },
}
</script>

<style scoped>

input[type='text'] {
    display: inline-block;
    width: inherit;
    padding: .5rem .75rem;
    border-radius: 4px;
    font-size: 100%;
}

.canvasContainer {
    width: 100%;
}

#pdfViewer {
    width: 100%;
}
.builder, #pdfCanvas, #watermark {
    position: absolute;
    visibility: collapse;
    display: table;
}

.pagination {
    display: flex;
    justify-content: end;

    &.top {
        margin-bottom: 1rem;
    }
    &.bottom {
        margin-top: 1rem;
        margin-bottom: 4rem;
    }
}

.loader {
    position: absolute;
}


</style>
